'use strict';

(() => {
  const normalize = (value) => String(value?.name ?? value ?? '');
  const match = (variants, choices) => variants.find((variant) => variant.options.every((option, index) => option === choices[index]));
  const compatible = (variants, choices, ignoredIndex = -1) => variants.filter((variant) => variant.options.every((option, index) => index === ignoredIndex || !choices[index] || option === choices[index]));

  function chooseOption(variants, choices, index, value) {
    const next = [...choices];
    next[index] = value;
    const candidates = variants.filter((variant) => variant.available && variant.options[index] === value);
    if (!candidates.length) return { choices: [...choices], selectedVariantId: match(variants, choices)?.available ? match(variants, choices).id : null };
    const score = (variant) => variant.options.reduce((total, option, optionIndex) => total + (optionIndex !== index && next[optionIndex] === option ? 1 : 0), 0);
    const best = candidates.reduce((current, candidate) => score(candidate) > score(current) ? candidate : current);
    for (let optionIndex = 0; optionIndex < next.length; optionIndex += 1) {
      if (optionIndex !== index && next[optionIndex] && next[optionIndex] !== best.options[optionIndex]) next[optionIndex] = null;
    }
    const exact = match(variants, next);
    return { choices: next, selectedVariantId: exact?.available ? exact.id : null };
  }

  function optionAvailable(variants, choices, index, value) {
    return variants.some((variant) => variant.options[index] === value && variant.available);
  }

  function initialState(variants, initialId, explicitId) {
    const explicit = variants.find((variant) => String(variant.id) === String(explicitId));
    const initial = variants.find((variant) => String(variant.id) === String(initialId));
    const variant = explicit || (initial?.available ? initial : null) || variants.find((candidate) => candidate.available) || initial || variants[0];
    return { initial: variant, choices: [...(variant?.options || [])], selectedVariantId: variant?.available ? variant.id : null };
  }

  function rows(variants, choices, optionValues, optionIndex) {
    if (optionIndex < 0) return variants.map((variant) => ({ label: variant.title, variant, available: Boolean(variant.available) }));
    return (optionValues[optionIndex] || []).map((rawValue) => {
      const value = normalize(rawValue); const next = [...choices]; next[optionIndex] = value; const variant = match(variants, next);
      return { label: value, variant, available: Boolean(variant?.available) };
    });
  }

  function selectVariant(variants, id) {
    const variant = variants.find((candidate) => String(candidate.id) === String(id));
    return variant?.available ? variant : null;
  }

  const quantity = (value) => Math.min(99, Math.max(1, Math.trunc(Number(value) || 1)));
  const total = (variant, amount) => (variant?.price || 0) * quantity(amount);
  function item(variant, amount) { return variant?.available ? { id: variant.id, quantity: quantity(amount) } : null; }
  const cartQuantity = (lines, variantId) => (Array.isArray(lines) ? lines : []).filter((line) => String(line.variant_id ?? line.variantId) === String(variantId)).reduce((sum, line) => sum + Math.max(0, Math.trunc(Number(line.quantity) || 0)), 0);

  function lowStock(inventory, threshold) {
    if (!inventory?.tracked || inventory.policy !== 'deny') return false;
    const quantityValue = Number(inventory.quantity);
    return Number.isFinite(quantityValue) && quantityValue > 0 && quantityValue <= Math.max(1, Number(threshold) || 1);
  }

  function shippingDate(now, days = 3) {
    const source = now instanceof Date ? now : new Date(now);
    if (Number.isNaN(source.getTime())) return null;
    const date = new Date(source.getFullYear(), source.getMonth(), source.getDate() + Number(days), 12, 0, 0, 0);
    const day = date.getDate();
    const mod100 = day % 100;
    const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
    const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
    return { iso: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`, label: `${day}${suffix} ${month}` };
  }

  function millisecondsUntilNextLocalDay(now) {
    const date = now instanceof Date ? now : new Date(now || Date.now());
    if (Number.isNaN(date.getTime())) return 60000;
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 2, 0);
    return Math.max(1000, next.getTime() - date.getTime());
  }

  function mediaKey(source) {
    return String(source || '').split('?')[0];
  }

  function mediaIds({ allMediaIds, selectedVariant, selectedColor, variantMediaById, mediaByColor }) {
    const all = [...new Set(allMediaIds.map(String))];
    const own = variantMediaById?.[String(selectedVariant?.id)];
    const colour = (mediaByColor?.[selectedColor] || []).map(String);
    const linked = new Set(Object.values(mediaByColor || {}).flat().map(String));
    const preferred = own ? String(own) : colour[0];
    const allowed = selectedColor && colour.length ? all.filter((id) => !linked.has(id) || colour.includes(id) || id === preferred) : all;
    return preferred && allowed.includes(preferred) ? [preferred, ...allowed.filter((id) => id !== preferred)] : allowed;
  }

  function backUrl(referrer, origin, currentUrl, fallback) {
    try {
      const url = new URL(referrer, origin);
      const current = new URL(currentUrl, origin);
      if (url.origin === origin && url.pathname !== current.pathname && /\/(?:collections(?:\/|$)|search(?:\/|$))/.test(url.pathname) && !/\/products\//.test(url.pathname)) return url.pathname + url.search + url.hash;
    } catch (_) {}
    return fallback;
  }

  const core = { normalize, match, compatible, chooseOption, optionAvailable, initialState, rows, selectVariant, quantity, total, item, cartQuantity, lowStock, shippingDate, millisecondsUntilNextLocalDay, mediaKey, mediaIds, backUrl };
  if (typeof module !== 'undefined') module.exports = core;
  if (typeof window !== 'undefined') window.OluvieReferenceProductCore = core;
})();
